import app from '../qwanyx-core/realm-config';

// Helper function to convert minutes to seconds with a default of 10 minutes
const getExpirationTime = (minutes = 10) => minutes * 60;

// Function to get pre-signed URLs from MongoDB Realm for different operations
const getPresignedUrl = async (fileName, operationType, expirationTimeInSeconds) => {
  try {
    // Fetch the pre-signed URL from your MongoDB Realm function
    const response = await app.currentUser.functions.generatePresignedUrl(
      'qwanyx-storage-images', 
      fileName, 
      operationType, 
      expirationTimeInSeconds
    );

    if (response.url) {
      return response.url; // Return the URL
    } else {
      throw new Error('No valid pre-signed URL returned');
    }
  } catch (error) {
    console.error('Error fetching pre-signed URL:', error);
    throw error;
  }
};

// Exponential backoff retry function
const retry = async (fn, retries = 3, delay = 1000, backoff = 2) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) {
      throw error;
    }
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    return retry(fn, retries - 1, delay * backoff, backoff);
  }
};

// Function to upload a single file with retries
const uploadSingleFile = async ({ file, key }, expirationTimeInSeconds) => {
  return retry(async () => {
    // Get the pre-signed URL for the file
    const presignedUrl = await getPresignedUrl(key, 'PUT', expirationTimeInSeconds);

    // Ensure the pre-signed URL is valid
    if (typeof presignedUrl !== 'string') {
      throw new Error(`Invalid pre-signed URL for ${key}`);
    }

    // Upload the file to the presigned URL with ACL set
    // Set correct Content-Type for SVG files
    const contentType = file.name.toLowerCase().endsWith('.svg') 
      ? 'image/svg+xml' 
      : (file.type || 'application/octet-stream');
      
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
        //'x-amz-acl': 'public-read' // Changed to public-read for consistency
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to upload ${key}: ${response.status} ${response.statusText}`);
    }
    
    return { key, success: true };
  });
};

// Function to upload multiple files
export const uploadFiles = async (files, expirationTime = 10) => {
  const timeInSeconds = getExpirationTime(expirationTime);
  const results = {
    successful: [],
    failed: []
  };

  // Process files sequentially to avoid overwhelming the server
  for (const fileData of files) {
    try {
      const result = await uploadSingleFile(fileData, timeInSeconds);
      results.successful.push(result.key);
      console.log(`${fileData.key} uploaded successfully`);
    } catch (error) {
      results.failed.push({
        key: fileData.key,
        error: error.message
      });
      console.error(`Error uploading ${fileData.key}:`, error);
    }
  }

  // Log summary
  console.log(`Upload complete. ${results.successful.length} succeeded, ${results.failed.length} failed.`);
  
  // Throw an error if any files failed, but include the partial results
  if (results.failed.length > 0) {
    const error = new Error('Some files failed to upload');
    error.results = results;
    throw error;
  }
  
  return results;
};

// Function to delete a single file with retries
const deleteSingleFile = async (fileName, expirationTimeInSeconds) => {
  return retry(async () => {
    // Get the pre-signed URL for deleting
    const presignedUrl = await getPresignedUrl(fileName, 'DELETE', expirationTimeInSeconds);

    // Perform the file deletion
    const response = await fetch(presignedUrl, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ${fileName}: ${response.status} ${response.statusText}`);
    }
    
    return { name: fileName, success: true };
  });
};

// Function to delete multiple files
export const deleteFiles = async (files, expirationTime = 10) => {
  const timeInSeconds = getExpirationTime(expirationTime);
  const results = {
    successful: [],
    failed: []
  };

  for (const file of files) {
    try {
      const result = await deleteSingleFile(file.name, timeInSeconds);
      results.successful.push(result.name);
      console.log(`${file.name} deleted successfully`);
    } catch (error) {
      results.failed.push({
        name: file.name,
        error: error.message
      });
      console.error(`Error deleting ${file.name}:`, error);
    }
  }

  console.log(`Deletion complete. ${results.successful.length} succeeded, ${results.failed.length} failed.`);
  
  if (results.failed.length > 0) {
    const error = new Error('Some files failed to delete');
    error.results = results;
    throw error;
  }
  
  return results;
};

// Function to download a single file with retries
const getSingleFile = async (fileName, expirationTimeInSeconds) => {
  return retry(async () => {
    // Get the pre-signed URL for downloading
    const presignedUrl = await getPresignedUrl(fileName, 'GET', expirationTimeInSeconds);

    // Perform the file download
    const response = await fetch(presignedUrl);

    if (!response.ok) {
      throw new Error(`Failed to download ${fileName}: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    return { name: fileName, blob, success: true };
  });
};

// Function to download multiple files
export const getFiles = async (files, expirationTime = 10) => {
  const timeInSeconds = getExpirationTime(expirationTime);
  const results = {
    successful: [],
    failed: []
  };

  for (const file of files) {
    try {
      const result = await getSingleFile(file.name, timeInSeconds);
      results.successful.push({
        name: file.name,
        blob: result.blob
      });
      console.log(`${file.name} downloaded successfully`);
    } catch (error) {
      results.failed.push({
        name: file.name,
        error: error.message
      });
      console.error(`Error downloading ${file.name}:`, error);
    }
  }

  console.log(`Download complete. ${results.successful.length} succeeded, ${results.failed.length} failed.`);
  
  if (results.failed.length > 0) {
    const error = new Error('Some files failed to download');
    error.results = results;
    throw error;
  }
  
  return results;
};